import { useRef, ReactNode } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

interface TiltCardProps {
  children: ReactNode;
  className?: string;
  maxTilt?: number;
  scale?: number;
  perspective?: number;
  glareEnabled?: boolean;
  glareMaxOpacity?: number;
}

/**
 * 3D Tilt Card Component
 * Creates a perspective tilt effect based on mouse position
 */
export function TiltCard({
  children,
  className = '',
  maxTilt = 15,
  scale = 1.02,
  perspective = 1000,
  glareEnabled = true,
  glareMaxOpacity = 0.15,
}: TiltCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Motion values for mouse position
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  // Spring config for smooth movement
  const springConfig = { stiffness: 300, damping: 25, restDelta: 0.001 };
  
  // Smooth spring-based rotation
  const rotateX = useSpring(
    useTransform(mouseY, [-0.5, 0.5], [maxTilt, -maxTilt]),
    springConfig
  );
  const rotateY = useSpring(
    useTransform(mouseX, [-0.5, 0.5], [-maxTilt, maxTilt]),
    springConfig
  );
  
  // Scale on hover
  const scaleValue = useMotionValue(1);
  const smoothScale = useSpring(scaleValue, { stiffness: 400, damping: 30 });
  
  // Glare effect position
  const glareX = useTransform(mouseX, [-0.5, 0.5], ['0%', '100%']);
  const glareY = useTransform(mouseY, [-0.5, 0.5], ['0%', '100%']);
  const glareOpacity = useMotionValue(0);
  const smoothGlareOpacity = useSpring(glareOpacity, { stiffness: 200, damping: 30 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    
    // Calculate normalized position (-0.5 to 0.5)
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseEnter = () => {
    scaleValue.set(scale);
    glareOpacity.set(glareMaxOpacity);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    scaleValue.set(1);
    glareOpacity.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      className={`relative ${className}`}
      style={{
        perspective,
        transformStyle: 'preserve-3d',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="relative w-full h-full"
        style={{
          rotateX,
          rotateY,
          scale: smoothScale,
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Card content */}
        {children}
        
        {/* Glare effect overlay */}
        {glareEnabled && (
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none overflow-hidden"
            style={{
              opacity: smoothGlareOpacity,
              background: `radial-gradient(
                circle at var(--glare-x) var(--glare-y),
                rgba(255, 255, 255, 0.4) 0%,
                transparent 60%
              )`,
              // @ts-ignore - CSS custom properties
              '--glare-x': glareX,
              '--glare-y': glareY,
            }}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

/**
 * Elevated content inside tilt card (appears to float above)
 */
export function TiltCardContent({
  children,
  className = '',
  elevation = 20,
}: {
  children: ReactNode;
  className?: string;
  elevation?: number;
}) {
  return (
    <div
      className={className}
      style={{
        transform: `translateZ(${elevation}px)`,
        transformStyle: 'preserve-3d',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Floating element that pops out of the card
 */
export function TiltCardFloating({
  children,
  className = '',
  elevation = 40,
}: {
  children: ReactNode;
  className?: string;
  elevation?: number;
}) {
  return (
    <div
      className={className}
      style={{
        transform: `translateZ(${elevation}px)`,
        transformStyle: 'preserve-3d',
      }}
    >
      {children}
    </div>
  );
}
